import React, { useState, useEffect } from 'react';
import { Box, FormControlLabel, Checkbox, Button, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { IconSizes } from 'types';
import useAssignments from 'state/hooks/use-assignments';
import {
  getIcon,
  isGroupSelected,
  isStudentSelected,
  determineIfPartialGroupSelected,
} from '../utils';
import AssignmentLetterLabels from '../../Assignments/components/AssignmentLetterLabels';

const tAssignment = (key, options?) => translate('assignment', key, options);

const GroupDropdown: React.FC<any> = ({ group }) => {
  const classes = useStyles();
  const [showClass, setShowClass] = useState(false);
  const [{ selectedGroups, selectedStudents }, { updateSelectedGroups, updateSelectedStudents }] =
    useAssignments();

  const groupId = JSON.stringify(group?.id);

  const icon = getIcon(group.name, IconSizes.LG);

  useEffect(() => {
    if (determineIfPartialGroupSelected(selectedStudents, selectedGroups, group))
      setShowClass(true);
  }, [selectedStudents]);

  const handleClick = () => setShowClass(!showClass);

  const handleGroupClick = (e) => {
    if (group.groupEnrollments.length) {
      const { id } = e.target;
      const groupId = Number(id);
      updateSelectedGroups(groupId);
    }
  };

  const handleStudentClick = (e) => {
    const { id } = e.target;
    const studentId = Number(id);
    updateSelectedStudents(studentId);
  };

  const renderStudentCountLabel = () => {
    if (!showClass || !determineIfPartialGroupSelected(selectedStudents, selectedGroups, group))
      return;
    const studentsSelected = group?.groupEnrollments
      ?.map((s) => {
        if (isStudentSelected(selectedStudents, s.studentId)) return s;
      })
      .filter((x) => x);

    return (
      <AssignmentLetterLabels
        groups={[]}
        students={studentsSelected}
        assignedToAllGroups={false}
        useSelectedLabel
      />
    );
  };

  const label = () => {
    return (
      <>
        <span className={classes.labelText}>{icon}</span>
        <span className={classes.labelText}>{group.name}</span>
        <span className={classes.labelText}>{tAssignment('group')}</span>
        <span className={classes.labelText}> {renderStudentCountLabel()}</span>
      </>
    );
  };

  const renderNoStudentsWarning = () => {
    return (
      <Box className={classes.warning}>
        <Typography variant="subtitle1">{tAssignment('noStudents')}</Typography>
      </Box>
    );
  };

  const renderStudents = () => {
    if (showClass) {
      if (!group?.groupEnrollments?.length) {
        return renderNoStudentsWarning();
      }
      return group.groupEnrollments.map((s) => {
        const { studentId } = s;
        const id = JSON.stringify(studentId);
        return (
          <Box key={id} className={classes.students}>
            <FormControlLabel
              id={id}
              onClick={handleStudentClick}
              control={
                <Checkbox id={id} checked={isStudentSelected(selectedStudents, studentId)} />
              }
              label={
                <Typography variant={'caption'}>
                  {s.student.firstName} {s.student.lastName}
                </Typography>
              }
            />
          </Box>
        );
      });
    }
  };

  return (
    <Box key={groupId} className={classes.groupDropdown}>
      <Box className={classes.padding}>
        <FormControlLabel
          id={groupId}
          onClick={handleGroupClick}
          control={<Checkbox id={groupId} checked={isGroupSelected(selectedGroups, group.id)} />}
          label={label()}
        />
        {renderStudents()}
      </Box>
      <Box className={classes.padding}>
        <Button onClick={handleClick}>
          {showClass ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  groupDropdown: {
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: '5px',
    marginBottom: remCalc(20),
    display: 'flex',
    justifyContent: 'space-between',
  },
  padding: {
    padding: 15,
  },
  labelText: {
    marginRight: 8,
  },
  students: {
    marginLeft: 25,
  },
  warning: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  pliText: {
    marginLeft: 60,
    color: theme.palette.link.main,
    fontSize: remCalc(14),
  },
}));

export default GroupDropdown;
